import React, {useEffect} from "react";
import {Link, useI18next, useTranslation} from "gatsby-plugin-react-i18next";
import {StaticImage} from "gatsby-plugin-image";
import logoLandscape from "../../images/logo-new/chatlab-landscape-tile.svg";
import facebookIcon from "../../images/icons/facebook.svg";
import linkedinIcon from "../../images/icons/linkedin.svg";
import xIcon from "../../images/icons/x.svg";
import aiActIcon from "../../images/icons/ai-act.svg";
import {getCookie} from "../../utils/siteInit";
import {INTEGRATIONS} from "../../components/shared/nav-items";

const LANGUAGE_CHOICE_COOKIE = "chatlab_lang";
const WIDGET_FREE_PATHS = ["/kwhotel", "/example/"];
// The Help Center index embeds the support bot as an inline iframe, so it must not also get the
// floating launcher. Every other /help/ page gets it.
const WIDGET_FREE_EXACT = ["/help", "/help/"];
const SUPPORT_BOT_API_KEY = "03fd69e3-51c7-4002-a414-404b23d4d906";
const SITE_DEFAULT_LANGUAGE = "en";

// Which language to declare to the widget - and when to say nothing at all.
// The English root is where every visitor lands when we have no version in their language, so it
// is a fallback, not a statement about what they read. The bot speaks far more languages than the
// site has versions, so for a Finn on chatlab.com the browser is the better signal and we stay
// quiet, letting the widget auto-detect. A non-default locale path, or an explicit pick in the
// language switcher (the chatlab_lang cookie, the same signal languageDetection.js trusts), IS a
// statement - then the widget follows the page.
const declaredLanguage = (language) => {
    const explicitPick = typeof window === "undefined" ? null : getCookie(LANGUAGE_CHOICE_COOKIE);
    if (explicitPick) return explicitPick;
    return language && language !== SITE_DEFAULT_LANGUAGE ? language : null;
};

const COMPARISONS = [
    {to: "/comparator/chatlab-chatbase/", label: "ChatLab vs Chatbase"},
    {to: "/comparator/chatlab-wonderchat/", label: "ChatLab vs Wonderchat"},
    {to: "/comparator/chatlab-sitegpt/", label: "ChatLab vs SiteGPT"},
    {to: "/comparator/chatlab-customgpt/", label: "ChatLab vs CustomGPT"},
    {to: "/comparator/chatlab-ibm+watson/", label: "ChatLab vs IBM Watson"},
    {to: "/comparator/chatlab-intercom/", label: "ChatLab vs Intercom Fin"},
];

const widgetAllowed = (path) => !WIDGET_FREE_EXACT.includes(path)
    && !WIDGET_FREE_PATHS.some((p) => path === p || path.startsWith(p) || path.startsWith(`${p}/`));

const isHelpPath = (path) => path === "/help" || path.startsWith("/help/");

const Column = ({title, children}) => (
    <div className="flex flex-col gap-4">
        <h3 className="cl-eyebrow text-[var(--cl-ink)]">{title}</h3>
        {children}
    </div>
);

const FooterLink = ({to, href, children}) => (
    <li>
        {href
            ? <a href={href} className="text-[15px] text-[var(--cl-ink-2)] hover:text-[var(--cl-ink)] hover:underline underline-offset-4">{children}</a>
            : <Link to={to} className="text-[15px] text-[var(--cl-ink-2)] hover:text-[var(--cl-ink)] hover:underline underline-offset-4">{children}</Link>}
    </li>
);

const Footer = () => {
    const {t} = useTranslation();
    const {language, originalPath} = useI18next();
    const path = originalPath || (typeof window !== "undefined" ? window.location.pathname : "");
    // Help Center articles are served by the dedicated support bot; the rest of the site by the
    // multilingual website bot.
    const apiKey = isHelpPath(path) ? SUPPORT_BOT_API_KEY : t("chatLabApiKey");

    useEffect(() => {
        if (typeof window === "undefined" || !widgetAllowed(originalPath || window.location.pathname)) return undefined;
        const load = () => {
            const existingChatDiv = document.getElementById("aichatbot_ra_div_id");
            if (existingChatDiv) existingChatDiv.remove();
            const existingScript = document.getElementById(apiKey);
            if (existingScript) existingScript.remove();
            window.aichatbotApiKey = apiKey;
            window.aichatbotProviderId = "f9e9c5e4-6d1a-4b8c-8d3f-3f9e9c5e46d1";
            // Only when the page language is a real signal about the visitor - otherwise leave it
            // unset so the widget falls back to their browser language.
            const declared = declaredLanguage(language);
            if (declared) {
                window.aichatbotLanguage = declared;
            } else {
                delete window.aichatbotLanguage;
            }
            const script = document.createElement("script");
            script.src = "https://script.chatlab.com/aichatbot.js";
            script.id = apiKey;
            script.defer = true;
            document.body.appendChild(script);
        };
        if ("requestIdleCallback" in window) {
            const id = window.requestIdleCallback(load, {timeout: 2000});
            return () => window.cancelIdleCallback(id);
        }
        const id = setTimeout(load, 2000);
        return () => clearTimeout(id);
    }, [apiKey, originalPath]);

    // The language switcher reloads the page, so the effect above normally covers it. This is for
    // the case where the locale changes while the widget is already mounted.
    useEffect(() => {
        if (typeof window === "undefined" || !language) return;
        const declared = declaredLanguage(language);
        if (!declared) return;
        window.aichatbotLanguage = declared;
        const chatbotApi = window.aichatbotApi && window.aichatbotApi.getChatbotApi(apiKey);
        if (chatbotApi && chatbotApi.setLanguage) {
            chatbotApi.setLanguage(declared);
        }
    }, [language, apiKey]);

    return (
        <footer id="footer" className="w-full border-t border-[var(--cl-rule)] bg-[var(--cl-surface)]">
            <div className="max-w-screen-2xl mx-auto px-5 py-12 flex flex-col gap-12">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-10">
                    <Column title={t("Product")}>
                        <ul className="flex flex-col gap-2">
                            <FooterLink to="/features/">{t("Features")}</FooterLink>
                            <FooterLink to="/pricing/">{t("Pricing")}</FooterLink>
                            <FooterLink to="/industry/ecommerce/">{t("For online stores")}</FooterLink>
                            <FooterLink to="/industry/hospitality/">{t("For hotels")}</FooterLink>
                            <FooterLink to="/help/">{t("Help Center")}</FooterLink>
                            <FooterLink to="/blog/">{t("Blog")}</FooterLink>
                            <FooterLink to="/press/">{t("Press & Media")}</FooterLink>
                        </ul>
                    </Column>

                    <Column title={t("Integrations")}>
                        <ul className="flex flex-col gap-2">
                            {INTEGRATIONS.map((i) => (
                                <FooterLink key={i.to} to={i.to}>{i.name}</FooterLink>
                            ))}
                        </ul>
                    </Column>

                    <Column title={t("Compare")}>
                        <ul className="flex flex-col gap-2">
                            {COMPARISONS.map((c) => (
                                <FooterLink key={c.to} to={c.to}>{c.label}</FooterLink>
                            ))}
                            <FooterLink to="/comparator/">{t("All comparisons")}</FooterLink>
                        </ul>
                    </Column>

                    <Column title={t("Company")}>
                        <ul className="flex flex-col gap-2">
                            <FooterLink to="/contact/">{t("Contact")}</FooterLink>
                            <FooterLink to="/agencypackage/">{t("Agency program")}</FooterLink>
                            <FooterLink to="/affiliates/">{t("Affiliate program")}</FooterLink>
                            <FooterLink to="/privacy/">{t("Privacy Policy")}</FooterLink>
                            <FooterLink to="/terms/">{t("Terms & Conditions")}</FooterLink>
                            <FooterLink to="/dpa/">DPA</FooterLink>
                            <FooterLink to="/subprocessors/">{t("Sub-processors")}</FooterLink>
                        </ul>
                    </Column>

                    <Column title={t("Contact")}>
                        <address className="not-italic text-[15px] leading-relaxed text-[var(--cl-ink-2)]">
                            ChatLab sp. z o.o.<br/>
                            Zamknięta 10/1.5<br/>
                            30-554 Kraków, {t("Poland")}<br/>
                            <a href="mailto:contact@chatlab.com" className="hover:underline underline-offset-4">contact@chatlab.com</a>
                        </address>
                        <div className="flex flex-col gap-3 mt-2">
                            <Link to="/blog/gdpr/" className="inline-block">
                                <StaticImage src="../../images/gdpr.webp" alt="GDPR" className="w-40" placeholder="none" formats={["webp"]}/>
                            </Link>
                            <Link to="/blog/ai-act-transparency/" className="inline-block">
                                <img src={aiActIcon} alt="EU AI Act transparency" className="w-40" width="160" height="53" loading="lazy"/>
                            </Link>
                        </div>
                    </Column>
                </div>

                <hr className="cl-rule"/>

                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <Link to="/" title={t("ChatLab - Custom ChatGPT for your website")}>
                            <img src={logoLandscape} className="w-[120px]" alt="ChatLab" width="120" height="33" loading="lazy"/>
                        </Link>
                        <p className="text-[13px] text-[var(--cl-muted)]">© {new Date().getFullYear()} {t("All rights reserved.")}</p>
                    </div>
                    <ul className="flex gap-5">
                        <li className="w-5 h-5"><a target="_blank" rel="noopener noreferrer" href="https://www.facebook.com/profile.php?id=61576535702089" aria-label="ChatLab on Facebook"><img src={facebookIcon} alt="Facebook" width="20" height="20" loading="lazy"/></a></li>
                        <li className="w-5 h-5"><a target="_blank" rel="noopener noreferrer" href="https://www.youtube.com/@ChatLabCom" aria-label="ChatLab on YouTube"><StaticImage src="../../images/youtube.webp" alt="YouTube" width={20} height={20} placeholder="none" formats={["webp"]}/></a></li>
                        <li className="w-5 h-5"><a target="_blank" rel="noopener noreferrer" href="https://www.linkedin.com/company/chatlab/" aria-label="ChatLab on LinkedIn"><img src={linkedinIcon} alt="LinkedIn" width="20" height="20" loading="lazy"/></a></li>
                        <li className="w-5 h-5"><a target="_blank" rel="noopener noreferrer" href="https://x.com/ChatLabCom" aria-label="ChatLab on X"><img src={xIcon} alt="X" width="20" height="20" loading="lazy"/></a></li>
                    </ul>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
